/**
 * Toast — minimal accessible notification primitive plus a ToastViewport
 * that renders a stack of toasts in the top-right corner. NL strings;
 * uses role=status/alert per severity per WAI-ARIA APG guidance.
 */
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from './icons';
import { cn } from './cn';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  variant?: ToastVariant;
  title: string;
  description?: string;
  /** Auto-dismiss after N ms; 0 to keep until closed. Defaults to 5000. */
  durationMs?: number;
  onDismiss?: (id: string) => void;
  action?: ReactNode;
}

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: 'border-pass-100 bg-pass-50 text-pass-700',
  info: 'border-info-100 bg-info-50 text-info-700',
  warning: 'border-warn-100 bg-warn-50 text-warn-700',
  error: 'border-fail-100 bg-fail-50 text-fail-700',
};

const VARIANT_ICON = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
} as const;

export function Toast({
  id,
  variant = 'info',
  title,
  description,
  durationMs = 5000,
  onDismiss,
  action,
}: ToastProps): JSX.Element {
  useEffect(() => {
    if (!durationMs || !onDismiss) return;
    const timer = window.setTimeout(() => onDismiss(id), durationMs);
    return () => window.clearTimeout(timer);
  }, [durationMs, id, onDismiss]);

  const Icon = VARIANT_ICON[variant];
  const isAlert = variant === 'error' || variant === 'warning';

  return (
    <div
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-2.5 rounded-md border bg-surface px-3 py-2.5 text-sm shadow-md',
        VARIANT_CLASS[variant],
      )}
    >
      <Icon size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-fg-primary">{title}</div>
        {description ? <p className="mt-0.5 text-xs text-fg-secondary">{description}</p> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss?.(id)}
        aria-label="Sluiten"
        className="shrink-0 text-fg-muted transition hover:text-fg-primary"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

export interface ToastViewportProps {
  toasts: readonly ToastProps[];
  className?: string;
}

export function ToastViewport({ toasts, className }: ToastViewportProps): JSX.Element {
  return (
    <div
      aria-label="Notificaties"
      className={cn(
        'pointer-events-none fixed right-4 top-4 z-toast flex flex-col gap-2',
        className,
      )}
    >
      {toasts.map((t) => (
        <Toast key={t.id} {...t} />
      ))}
    </div>
  );
}

// The `toast()` singleton helper lives in `lib/toast.ts` so this file
// stays a components-only module (keeps react-refresh happy).

/**
 * useToasts — tiny in-memory toast store hook. Screens may either consume
 * this directly or wire up a context provider on top.
 */
export function useToasts(): {
  toasts: ToastProps[];
  push: (toast: Omit<ToastProps, 'id' | 'onDismiss'>) => string;
  dismiss: (id: string) => void;
} {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onDismiss'>): string => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { ...toast, id, onDismiss: dismiss }]);
      return id;
    },
    [dismiss],
  );

  return { toasts, push, dismiss };
}
